import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import {
	BillingFeature,
	BlockVisualization,
	PopupContextSwitchAction as ContextSwitchAction,
} from 'webapp/enums/enums';
import { copyObject } from 'webapp/utils/common';

import BlocktypePickerComponent from 'webapp/components/blocktype-picker';
// Extend this to facilitate creating a Block on a Board from diff features
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormSelectComponent from 'webapp/components/form/select';
import type { OptionRaw } from 'webapp/components/form/select';
import FormSelectBoardWithCreateNewOptionComponent, {
	NewBoardId,
} from 'webapp/components/form/select-board-with-create-new-option';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type Block from 'webapp/models-custom/block';

import type { onConnectSourceResult } from 'webapp/services/connect';

export default abstract class BlockPopupCreateComponent extends PopupInnerModalComponent<PopupData.Block.Create> {
	@service('request') declare requestService: Service.Request;
	@service('billing') declare billingService: Service.Billing;
	@service('tour') declare tourService: Service.Tour;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;

	@tracked selectedSourceId = '';
	@tracked selectedBoardId = NewBoardId;

	@tracked blockType = BlockVisualization.BAR_CHART;

	abstract get canChangeSource(): boolean;

	abstract get datablockName(): string;

	abstract get showBlockTypePicker(): boolean;

	abstract get isAttributed(): boolean;

	abstract createDatablock(boardId: string): void | Promise<unknown>;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (this.popupData.selectedSourceId) {
			this.selectedSourceId = this.popupData.selectedSourceId;
		}
	}

	get sourceType() {
		return this.popupData.genericSource;
	}

	get createdNow() {
		return !!this.popupData.createdNow;
	}

	get trackOrigin() {
		return this.popupData.trackOrigin;
	}

	get restrictedBlockTypes(): BlockVisualization[] | undefined {
		return undefined; // defaults in BlocktypePicker component
	}

	get sourcesOfType() {
		const sourceTypeId = this.sourceType?.id;
		if (sourceTypeId) {
			return this.mmSourcesService.sourceListSpecific.filter(
				(item) => item.type === sourceTypeId,
			);
		} else {
			return [];
		}
	}

	@cached
	get sourcesList() {
		const datasources: OptionRaw<string>[] = copyObject(this.sourcesOfType).map((source) => {
			return {
				...source,
				disabled: source.isDisabled,
			};
		});

		datasources.forEach((ds) => {
			ds.group = '';
		});

		datasources.push({
			value: 'new',
			label: `Connect another ${this.sourceType?.name}`,
		});

		return datasources;
	}

	get selectedSource() {
		let source;
		if (this.selectedSourceId) {
			source = this.sourcesList.find((item) => item.value === this.selectedSourceId);
		}
		return source;
	}

	get showSourceConnect() {
		return this.canChangeSource && this.sourcesOfType.length === 0;
	}

	get isSubmitDisabled() {
		return this.showSourceConnect || this.selectedSourceId === 'new';
	}

	@action
	changeSource(sourceId: string) {
		this.selectedSourceId = sourceId;
		if (this.selectedSourceId === 'new') {
			this.connectNew();
		}
	}

	@action
	changeSelectedBoardId(boardId: string) {
		this.selectedBoardId = boardId;
	}

	@action
	changeBlockType(blockType: BlockVisualization) {
		this.blockType = blockType;
	}

	@action
	connectNew() {
		void this.sourceType
			?.connectNew({ origin: 'createBlock' })
			.then((result: onConnectSourceResult) => {
				if (result.isSuccess) {
					this.changeSource(String(result.source.id));
				}
			});
	}

	@action
	confirm() {
		if (!this.selectedBoardId) {
			return;
		}

		if (this.selectedBoardId === NewBoardId) {
			if (this.billingService.checkIfCan(BillingFeature.BOARDS, true)) {
				const data = {
					primaryView: 7,
				};

				this.submitInProgress = true;

				const board = this.mmBoardsService.createNewModel(data);
				board
					.save()
					.then((boardRecord) => {
						next(() => {
							const spaceAccessIds = this.getSelectedSourceIds();
							const datasources: string[] = [];
							spaceAccessIds.forEach((spaceAccessId) => {
								const sourceTypeKey = this.mmSourcesService.getSourceTypeKey(
									String(spaceAccessId),
								);
								if (sourceTypeKey) {
									datasources.push(sourceTypeKey);
								}
							});
							this.mmBoardsService.triggerDataboardCreated(
								{ from: this.trackOrigin, datasources },
								board,
							);
							void this.createDatablock(boardRecord.id!);
						});
					})
					.finally(() => {
						this.submitInProgress = false;
					})
					.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
			}
		} else {
			void this.createDatablock(this.selectedBoardId);
		}
	}

	getSelectedSourceIds(): (number | string)[] {
		const selectedSource = this.selectedSourceId;
		return selectedSource ? [this.selectedSourceId] : [];
	}

	confirmRedirect(block: Block) {
		const board = this.mmBoardsService.findModel(String(block.boardId));

		let absoluteUrl = this.navigationService.getAbsoluteUrlForRoute(
			'designer.board',
			String(block.boardId),
		);
		let route = `/layout-designer/${block.boardId}`;

		if (
			this.currentUserService.loggedInAsPowerUser ||
			!this.tourService.isEligibleForDesignerIntroTour
		) {
			// if on tour, must not have a block selected
			absoluteUrl += '/' + block.id;
			route += '/' + block.id;
		}

		this.navigationService.createContextSwitchModal().open({
			actionType: ContextSwitchAction.ADD_TO,
			sourceItem: block,
			targetItem: board,
			redirectUrl: this.popupData.openInNewTab ? absoluteUrl : undefined,
			onSwitch: () => {
				this.navigationService.replaceRoute(route);
			},
		});
	}
	<template>
		<div class="title">
			{{#if this.createdNow}}
				<CommonUiIconComponent @name="Metrics" @size="lg" />
				You successfully created a new metric!
			{{else}}
				<CommonUiIconComponent @name="Databoards" @size="lg" />
				Add datablock "{{this.datablockName}}" to dashboard
			{{/if}}
		</div>
		<div class="content">
			{{#if this.canChangeSource}}
				<div class="source">
					{{#if this.showSourceConnect}}
						<p>
							Connect
							{{this.sourceType.name}}
							to use this datablock:
						</p>
						<a role="button" class="button theme-green" {{on "click" this.connectNew}}>
							<CommonUiIconComponent @name="Connect" @size="sm" />Connect
						</a>
					{{else}}
						<p>
							Select data source you want to be used with this datablock:
						</p>
						<div class="row">
							<FormSelectComponent
								name="source"
								@options={{this.sourcesList}}
								@selected={{this.selectedSourceId}}
								@onChange={{this.changeSource}}
								@isNumeric={{false}}
								@isRequired={{true}}
								@tooltipOnSelectionFromProperty=""
								@isDisabled={{this.submitInProgress}}
							/>
						</div>
					{{/if}}
				</div>
			{{/if}}
			<div class="board">
				<p>
					Where would you like to add this metric to?
				</p>

				<FormSelectBoardWithCreateNewOptionComponent
					@changeSelectedBoardId={{this.changeSelectedBoardId}}
				/>

			</div>

			{{#if this.showBlockTypePicker}}

				<div class="board">
					<p>
						How would you like to visualize data?
					</p>
					<div class="row">
						<BlocktypePickerComponent
							@blockType={{this.blockType}}
							@restrictedBlockTypes={{this.restrictedBlockTypes}}
							@isAttributed={{this.isAttributed}}
							@onChangeBlockType={{this.changeBlockType}}
						/>
					</div>
				</div>

			{{/if}}

			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text={{if this.createdNow "No, I will add it manually later" "Cancel"}}
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Add to dashboard"
						@extraClass="save"
						@onTrigger={{this.confirm}}
						@submitInProgress={{this.submitInProgress}}
						@isDisabled={{this.isSubmitDisabled}}
					/>
				</div>
			</div>

		</div>
	</template>
}
